import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Send,
  Loader2,
  Music2,
  Sparkles,
  User,
  Bot,
  Crown,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssistantPage = () => {
  const [searchParams] = useSearchParams();
  const { user, getAuthHeaders } = useAuth();
  const messagesEndRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysisContext, setAnalysisContext] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [suggestions, setSuggestions] = useState([
    "Que sustituciones puedo usar para esta progresion?",
    "Sugiere acordes mas interesantes",
    "Como puedo hacer que suene mas jazzy?",
    "Explica la funcion de cada acorde",
  ]);

  const isPro = (user?.plan || "free") === "pro";
  const analysisId = searchParams.get("analysisId");

  const fetchUsageSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/usage`, {
        headers: getAuthHeaders(),
      });
      setUsageSummary(response.data);
    } catch (error) {
      console.error("Error fetching assistant usage:", error);
    }
  }, [getAuthHeaders]);

  const fetchAnalysisContext = useCallback(async (analysisId) => {
    try {
      const response = await axios.get(`${API_URL}/analysis/${analysisId}`, {
        headers: getAuthHeaders(),
      });
      setAnalysisContext(response.data);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `He cargado el analisis de "${response.data.filename}": Key ${response.data.key}, BPM ${response.data.bpm}, acordes: ${response.data.chords
            .slice(0, 8)
            .map((c) => c.chord)
            .join(" - ")}.`,
        },
      ]);
    } catch (error) {
      console.error("Error fetching analysis:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchUsageSummary();
    if (analysisId) {
      fetchAnalysisContext(analysisId);
    }

    setMessages([
      {
        role: "assistant",
        content:
          "Hola. Soy tu asistente armonico de SteelMora. Puedo ayudarte con analisis de acordes, rearmonizacion, puentes y variaciones. En que te ayudo hoy?",
      },
    ]);
  }, [analysisId, fetchAnalysisContext, fetchUsageSummary]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (message = input) => {
    if (!message.trim() || loading) return;

    const userMessage = { role: "user", content: message };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/assistant/chat`,
        {
          message,
          context: analysisContext
            ? {
                key: analysisContext.key,
                bpm: analysisContext.bpm,
                chords: analysisContext.chords.slice(0, 16),
              }
            : null,
          history: messages.slice(-10),
        },
        {
          headers: getAuthHeaders(),
        }
      );

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: response.data.response,
        },
      ]);

      if (response.data.suggestions?.length > 0) {
        setSuggestions(response.data.suggestions);
      }
      await fetchUsageSummary();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403) {
        toast.error(detail || "Limite Free alcanzado. Mejora a Pro para continuar.");
      } else {
        toast.error("Error al comunicarse con el asistente");
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            detail ||
            "Lo siento, hubo un error al procesar tu mensaje. Intenta de nuevo.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col">
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard"
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-white font-['Manrope']">
                  Asistente Armonico
                </h1>
                <p className="text-xs text-gray-500">Potenciado por GPT-5.2</p>
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 bg-black/20">
            <Crown className={`w-4 h-4 ${isPro ? "text-yellow-400" : "text-gray-500"}`} />
            <span
              className={`text-xs font-semibold uppercase ${
                isPro ? "text-yellow-300" : "text-gray-400"
              }`}
            >
              {isPro ? "Pro" : "Free"}
            </span>
            {!isPro && (
              <span className="text-xs text-gray-500">
                {usageSummary?.assistant_daily?.remaining ?? 10} prompts hoy
              </span>
            )}
            {isPro && usageSummary && (
              <span className="text-xs text-gray-500">
                {usageSummary.remaining.assistant} prompts este mes
              </span>
            )}
          </div>

          {analysisContext && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg border border-primary/20">
              <Music2 className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-mono">{analysisContext.key}</span>
              <span className="text-xs text-gray-500">|</span>
              <span className="text-sm text-gray-400 font-mono">
                {analysisContext.bpm} BPM
              </span>
            </div>
          )}
        </div>
      </header>

      <ScrollArea className="flex-1 px-6 py-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {messages.map((message, i) => (
            <div
              key={i}
              className={`flex gap-4 ${message.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`message-${i}`}
            >
              {message.role === "assistant" && (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-primary" />
                </div>
              )}

              <div className={`max-w-[80%] ${message.role === "user" ? "order-first" : ""}`}>
                <Card
                  className={`${
                    message.role === "user"
                      ? "bg-primary/20 border-primary/30"
                      : "bg-[#18181b] border-white/10"
                  }`}
                >
                  <CardContent className="p-4">
                    <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">
                      {message.content}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {message.role === "user" && (
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-primary" />
              </div>
              <Card className="bg-[#18181b] border-white/10">
                <CardContent className="p-4 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  <span className="text-gray-400">Pensando...</span>
                </CardContent>
              </Card>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {suggestions.length > 0 && messages.length < 3 && (
        <div className="px-6 py-4 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs text-gray-500 mb-3">Sugerencias</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="border-white/10 text-gray-300 hover:text-white hover:border-primary/50 text-left"
                  data-testid={`suggestion-${i}`}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="sticky bottom-0 px-6 py-4 glass-panel">
        <div className="max-w-4xl mx-auto">
          {!isPro && (
            <p className="text-xs text-amber-400 text-center mb-2">
              Plan Free: {usageSummary?.assistant_daily?.remaining ?? 10} mensajes restantes hoy. Pro desbloquea uso extendido.
            </p>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="flex gap-3"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pregunta sobre acordes, progresiones, teoria musical..."
              className="flex-1 bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-primary hover:bg-primary/90 btn-glow h-12 px-6"
              data-testid="send-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </form>
          <p className="text-xs text-gray-500 text-center mt-3">
            El asistente puede cometer errores. Verifica la informacion importante.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AssistantPage;
